import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { MultiSelectCheckbox } from "@/components/ui/multi-select-checkbox";
import { getSelectProductGroups } from "@/Api/productGroups";
import { getSelectSellers } from "@/Api/sellers";
import { getSelectUnities } from "@/Api/unities";
import { ClientResultsParams } from "@/Api/clientResults";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";

export const LEGAL_NOTICE_TITLE = "Informações sobre este demonstrativo";

const pad = (n: number) => String(n).padStart(2, "0");

export const firstDayOfMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-01`;
};

export const lastDayOfMonth = () => {
  const d = new Date();
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return `${last.getFullYear()}-${pad(last.getMonth() + 1)}-${pad(last.getDate())}`;
};

/** "2026-10-06" -> "06/10/2026" sem passar por Date (evita fuso) */
export const formatDateBr = (value?: string | null) => {
  if (!value) return "-";
  const [y, m, d] = value.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
};

export type ClientResultsFilterState = {
  date_start: string;
  date_end: string;
  lottery_id: string[];
  contest_names: string;
  seller_id: string[];
  unity_id: string[];
  search: string;
};

export const defaultFilters = (): ClientResultsFilterState => ({
  date_start: firstDayOfMonth(),
  date_end: lastDayOfMonth(),
  lottery_id: [],
  contest_names: "",
  seller_id: [],
  unity_id: [],
  search: "",
});

export const toParams = (f: ClientResultsFilterState): ClientResultsParams => ({
  date_start: f.date_start || undefined,
  date_end: f.date_end || undefined,
  lottery_id: f.lottery_id.length ? f.lottery_id : undefined,
  contest_names: f.contest_names || undefined,
  seller_id: f.seller_id.length ? f.seller_id : undefined,
  unity_id: f.unity_id.length ? f.unity_id : undefined,
  search: f.search || undefined,
});

/** Filtros <-> query string, para o detalhe abrir com o mesmo período/loterias da lista */
export const filtersToSearch = (f: ClientResultsFilterState) => {
  const q = new URLSearchParams();
  if (f.date_start) q.set("date_start", f.date_start);
  if (f.date_end) q.set("date_end", f.date_end);
  if (f.lottery_id.length) q.set("lottery_id", f.lottery_id.join(","));
  if (f.contest_names) q.set("contest_names", f.contest_names);
  if (f.seller_id.length) q.set("seller_id", f.seller_id.join(","));
  if (f.unity_id.length) q.set("unity_id", f.unity_id.join(","));
  if (f.search) q.set("search", f.search);
  return q.toString();
};

export const filtersFromSearch = (q: URLSearchParams): ClientResultsFilterState => {
  const list = (key: string) => (q.get(key) ? q.get(key)!.split(",").filter(Boolean) : []);
  const defaults = defaultFilters();
  return {
    date_start: q.get("date_start") || defaults.date_start,
    date_end: q.get("date_end") || defaults.date_end,
    lottery_id: list("lottery_id"),
    contest_names: q.get("contest_names") || "",
    seller_id: list("seller_id"),
    unity_id: list("unity_id"),
    search: q.get("search") || "",
  };
};

const isHexColor = (c?: string | null) => !!c && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(c);

export const lotteryColor = (c?: string | null) => (isHexColor(c) ? (c as string) : "#475569");

/** Fundo suave a partir da cor da loteria (#RRGGBB + alpha) */
export const lotterySoftBg = (c?: string | null) => {
  const color = lotteryColor(c);
  return color.length === 7 ? `${color}1F` : "#47556920";
};

export const LotteryChip = ({
  lotteryName,
  contestName,
  color,
  pending,
}: {
  lotteryName: string;
  contestName: string;
  color?: string | null;
  pending?: boolean;
}) => (
  <span
    className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium whitespace-nowrap"
    style={{ backgroundColor: lotterySoftBg(color), color: lotteryColor(color) }}
    title={pending ? "Concurso ainda não apurado" : undefined}
  >
    {lotteryName} {contestName}
    {pending && (
      <span className="ml-1 rounded bg-red-100 px-1 text-[10px] font-semibold text-red-700 dark:bg-red-900/40 dark:text-red-300">
        Não apurado
      </span>
    )}
  </span>
);

type Option = { value: string; label: string };

interface FiltersBarProps {
  value: ClientResultsFilterState;
  onChange: (value: ClientResultsFilterState) => void;
  onApply: () => void;
  onClear: () => void;
  /** Gerência/admin: mostra filtros de vendedor e unidade */
  showSellerFilters: boolean;
  /** Na tela do cliente a busca por nome não faz sentido */
  showSearch?: boolean;
  loading?: boolean;
}

export const FiltersBar = ({
  value,
  onChange,
  onApply,
  onClear,
  showSellerFilters,
  showSearch = true,
  loading,
}: FiltersBarProps) => {
  const [lotteries, setLotteries] = useState<Option[]>([]);
  const [sellers, setSellers] = useState<Option[]>([]);
  const [unities, setUnities] = useState<Option[]>([]);

  useEffect(() => {
    (async () => {
      const { data, ok } = await getSelectProductGroups();
      if (ok && data?.response) {
        setLotteries(
          data.response.map((l: { id: number; name: string }) => ({ value: String(l.id), label: l.name }))
        );
      }
    })();
  }, []);

  useEffect(() => {
    if (!showSellerFilters) return;
    (async () => {
      const [sellersRes, unitiesRes] = await Promise.all([
        getSelectSellers({ is_active: true }),
        getSelectUnities(),
      ]);
      if (sellersRes.ok) {
        setSellers(
          (sellersRes.data?.response || []).map((s: { id: number; user?: { name: string } }) => ({
            value: String(s.id),
            label: s.user?.name || `Vendedor ${s.id}`,
          }))
        );
      }
      if (unitiesRes.ok) {
        setUnities(
          (unitiesRes.data?.response || []).map((u: { id: number; name: string }) => ({
            value: String(u.id),
            label: u.name,
          }))
        );
      }
    })();
  }, [showSellerFilters]);

  const set = <K extends keyof ClientResultsFilterState>(key: K, v: ClientResultsFilterState[K]) =>
    onChange({ ...value, [key]: v });

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <p className="text-sm font-semibold">Filtros</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <div className="space-y-1">
            <Label htmlFor="cr-date-start">Data de validade inicial</Label>
            <Input
              id="cr-date-start"
              type="date"
              value={value.date_start}
              onChange={(e) => set("date_start", e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="cr-date-end">Data de validade final</Label>
            <Input
              id="cr-date-end"
              type="date"
              value={value.date_end}
              onChange={(e) => set("date_end", e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label>Loteria</Label>
            <MultiSelectCheckbox
              options={lotteries}
              selectedValues={value.lottery_id}
              onSelectionChange={(v) => set("lottery_id", v)}
              placeholder="Todas as loterias"
              selectAllLabel="SELECIONAR TUDO"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="cr-contests">Concursos</Label>
            <Input
              id="cr-contests"
              placeholder="Ex.: 3798, 3064, 7000"
              value={value.contest_names}
              onChange={(e) => set("contest_names", e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && onApply()}
            />
          </div>
          {showSearch && (
            <div className="space-y-1">
              <Label htmlFor="cr-search">Cliente</Label>
              <Input
                id="cr-search"
                placeholder="Nome, CPF ou telefone"
                value={value.search}
                onChange={(e) => set("search", e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && onApply()}
              />
            </div>
          )}
          {showSellerFilters && (
            <>
              <div className="space-y-1">
                <Label>Vendedor</Label>
                <MultiSelectCheckbox
                  options={sellers}
                  selectedValues={value.seller_id}
                  onSelectionChange={(v) => set("seller_id", v)}
                  placeholder="Todos os vendedores"
                  selectAllLabel="SELECIONAR TUDO"
                  searchable
                  searchPlaceholder="Buscar vendedor"
                />
              </div>
              <div className="space-y-1">
                <Label>Unidade</Label>
                <MultiSelectCheckbox
                  options={unities}
                  selectedValues={value.unity_id}
                  onSelectionChange={(v) => set("unity_id", v)}
                  placeholder="Todas as unidades"
                  selectAllLabel="SELECIONAR TUDO"
                />
              </div>
            </>
          )}
          <div className="flex items-end gap-2 sm:col-span-2 xl:col-span-1">
            <Button className={cn(colors.bg_primary, "flex-1")} onClick={onApply} disabled={loading}>
              Aplicar filtros
            </Button>
            <Button variant="outline" className="flex-1" onClick={onClear} disabled={loading}>
              Limpar
            </Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">Período pela data de validade dos concursos</p>
      </CardContent>
    </Card>
  );
};
